import React from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import DragAndDropButtonGroup from '../edit-panel/components/DragAndDropButtonGroup';

const AuthSequencingItem = React.memo(
  ({
    canEdit,
    isDragging,
    isChanged,
    sequenced,
    futureSequenced,
    index,
    inventoryOwner,
    isActive,
    supplierDirectedBlend,
    onAddSeq,
    onRemoveSeq,
  }) => {
    const isHighlighted = isDragging || isChanged;

    return (
      <Stack
        alignItems="center"
        direction="row"
        gap={1}
        sx={{
          backgroundColor: isHighlighted
            ? '_next.warning._states.selected'
            : '_next.common.white._states.main',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderTop: isHighlighted ? '1px solid' : 'none',
          borderTopColor: isHighlighted ? '_next.divider' : 'transparent',
          boxShadow: isDragging ? 11 : 'none',
          px: 2,
          py: 1,
          transform: isDragging ? 'scale(0.95)' : 'scale(1)',
          transition: 'transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
        }}
      >
        {futureSequenced ? (
          <Chip
            label="Future"
            sx={{
              backgroundColor: '_next.secondary.main',
              color: '_next.secondary.contrastText',
              fontFamily: 'Overpass',
              height: '24px',
              pt: '2px',
            }}
            variant="subtitle2"
          />
        ) : (
          <Chip
            label={isActive ? 'On' : 'Off'}
            sx={{
              backgroundColor: isActive ? 'secondary.main' : 'error.main',
              color: isActive ? 'secondary.contrastText' : 'error.contrastText',
              fontFamily: 'Overpass',
              height: '24px',
              pt: '2px',
            }}
            variant="subtitle2"
          />
        )}
        {sequenced && (
          <Typography color="text.primary" variant={index === 0 ? 'subtitle2' : 'body3'}>
            {`${index + 1}`}
          </Typography>
        )}
        {inventoryOwner && (
          <Typography
            flex="1"
            sx={{ color: 'text.primary' }}
            variant={sequenced && index === 0 ? 'subtitle2' : 'body3'}
          >
            {supplierDirectedBlend ? 'Supplier Directed Blend' : inventoryOwner}
          </Typography>
        )}
        {canEdit && (
          <DragAndDropButtonGroup
            idx={index}
            mode={sequenced ? 'remove' : 'add'}
            onAddSeq={onAddSeq}
            onRemoveSeq={onRemoveSeq}
            sx={{ alignSelf: 'flex-end', ml: 'auto' }}
          />
        )}
      </Stack>
    );
  }
);

AuthSequencingItem.propTypes = {
  canEdit: PropTypes.bool,
  futureSequenced: PropTypes.bool,
  index: PropTypes.number.isRequired,
  inventoryOwner: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  isChanged: PropTypes.bool,
  isDragging: PropTypes.bool,
  onAddSeq: PropTypes.func,
  onRemoveSeq: PropTypes.func,
  // this has been unreliable in determining order, but keeping it here in case we want to use it in the future
  // eslint-disable-next-line react/no-unused-prop-types
  sequenceNumber: PropTypes.number,
  sequenced: PropTypes.bool,
  supplierDirectedBlend: PropTypes.bool,
};

AuthSequencingItem.defaultProps = {
  canEdit: false,
  futureSequenced: false,
  isActive: false,
  isChanged: false,
  isDragging: false,
  onAddSeq: null,
  onRemoveSeq: null,
  sequenceNumber: null,
  sequenced: false,
  supplierDirectedBlend: false,
};

export default AuthSequencingItem;
